import { useTranslation } from "react-i18next";

import { Badge } from "components/ui/badge";
import { Switch } from "components/ui/switch";
import { USAGE_LIMITS_I18N } from "features/usage-config/constants/usage-meters";

import { UsageCardFrame, UsageFieldOrigin } from "./usage-card-frame";

const T = USAGE_LIMITS_I18N;

export interface BlockedControl {
  value: boolean;
  inherited?: boolean;
  onChange: (blocked: boolean) => void;
  onFollowTenant?: () => void;
}

export interface EnforcementSwitchCardProps {
  description: string;
  enforcing: boolean;
  onEnforcingChange: (enforcing: boolean) => void;
  enforcementInherited?: boolean;
  onFollowEnforcement?: () => void;
  blocked?: BlockedControl;
  showOffNote: boolean;
  readOnly?: boolean;
}

export const EnforcementSwitchCard = ({
  description,
  enforcing,
  onEnforcingChange,
  enforcementInherited,
  onFollowEnforcement,
  blocked,
  showOffNote,
  readOnly,
}: EnforcementSwitchCardProps) => {
  const { t } = useTranslation();

  return (
    <UsageCardFrame cardId="enforcement" label={t(T.ENFORCEMENT_LABEL)}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
        <p className="min-w-0 text-sm text-muted-foreground">{description}</p>
        <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-3 md:justify-end">
          {blocked && (
            <div className="flex shrink-0 items-center gap-3 whitespace-nowrap">
              <UsageFieldOrigin
                inherited={blocked.inherited}
                onFollowTenant={blocked.onFollowTenant}
                readOnly={readOnly}
              />
              <Badge variant={blocked.value ? "destructive" : "outline"}>
                {blocked.value ? t(T.BLOCKED) : t(T.NOT_BLOCKED)}
              </Badge>
              <Switch
                checked={!blocked.value}
                onCheckedChange={(active) => blocked.onChange(!active)}
                disabled={readOnly}
                aria-label={t(T.ACTIVE_LABEL)}
              />
            </div>
          )}
          <div className="flex shrink-0 items-center gap-3 whitespace-nowrap">
            <UsageFieldOrigin
              inherited={enforcementInherited}
              onFollowTenant={onFollowEnforcement}
              readOnly={readOnly}
            />
            <Badge variant={enforcing ? "default" : "outline"}>
              {enforcing ? t(T.ENFORCED) : t(T.NOT_ENFORCED)}
            </Badge>
            <Switch
              checked={enforcing}
              onCheckedChange={onEnforcingChange}
              disabled={readOnly}
              aria-label={t(T.ENFORCEMENT_LABEL)}
            />
          </div>
        </div>
      </div>

      {blocked?.value && (
        <p className="rounded-sm bg-muted px-3 py-2 text-sm text-destructive">{t(T.BLOCKED_NOTE)}</p>
      )}

      {showOffNote && (
        <p className="rounded-sm bg-muted px-3 py-2 text-sm text-muted-foreground">
          {t(T.ENFORCEMENT_OFF_NOTE)}
        </p>
      )}
    </UsageCardFrame>
  );
};
